import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDate, PROJECT_STATUS, MILESTONES } from "@/lib/constants";
import { RiskDot } from "@/components/RiskDot";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, FileArrowUp, Trash } from "@phosphor-icons/react";
import { Progress } from "@/components/ui/progress";

function ImportOutlookDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPoll(), []);

  const pollJob = (jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/import-jobs/${jobId}`);
        setJob(data);
        if (data.status === "completed" || data.status === "error") stopPoll();
      } catch { stopPoll(); }
    }, 1500);
  };

  const reset = () => { stopPoll(); setResult(null); setJob(null); setBusy(false); if (fileRef.current) fileRef.current.value = ""; };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setResult(null); setJob(null);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("code", code); fd.append("name", name);
      const { data } = await api.post("/projects/import-outlook-zip", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      onDone && onDone();
      if (data.job_id) { setJob({ status: "processing", total: 0, done: 0 }); pollJob(data.job_id); }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const projs = result?.projects || [];
  const done = job?.done || 0;
  const total = job?.total || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : (job?.status === "completed" ? 100 : 0);
  const finished = job?.status === "completed" || job?.status === "error";

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="import-outlook-btn"><FileArrowUp size={16} className="mr-1.5" /> Importă dosar proiect vechi</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Importă dosarul unui proiect vechi (arhivă .zip)</DialogTitle></DialogHeader>

        {!result ? (
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Cod proiect (opțional)</Label><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ex: HGS-1856" data-testid="import-code" /></div>
              <div className="grid gap-1.5"><Label>Nume (opțional)</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nume proiect" data-testid="import-name" /></div>
            </div>
            <p className="text-xs text-muted-foreground">Pune într-o arhivă .zip tot dosarul proiectului vechi: emailuri Outlook (.msg) ȘI/SAU orice documente (PDF, Word, Excel, imagini, DWG/STEP, avize, facturi). Emailurile → „EMAIL-URI PROIECT", documentele → subfoldere pe departament. <b>Lasă codul gol</b> ca o arhivă să conțină <b>mai multe proiecte</b> — fiecare fișier merge la codul detectat (din denumirea folderului, subiect sau denumire). Emailurile și documentele (PDF/Word) sunt clasificate și rezumate automat de AI, în fundal.</p>
            <input ref={fileRef} type="file" accept=".zip" className="hidden" onChange={onPick} data-testid="import-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-upload-btn">{busy ? "Se importă…" : "Alege arhiva .zip și importă"}</Button>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="import-result">
            <div className="rounded-lg border border-border p-3 text-sm">
              <div className="font-semibold">Import finalizat{projs.length > 1 ? ` în ${projs.length} proiecte` : ""}:</div>
              <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                {projs.map((p) => (
                  <li key={p.project_id}>• <b className="text-foreground">{p.code}</b> — {p.emails} emailuri, {p.attachments} documente{p.created ? " (proiect nou)" : ""}</li>
                ))}
              </ul>
            </div>

            <div data-testid="import-progress">
              <div className="mb-1.5 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {finished
                    ? (job?.status === "error" ? "Clasificarea AI a întâmpinat o eroare." : (total > 0 ? `Clasificare AI finalizată — ${total} elemente.` : "Nimic de clasificat."))
                    : (total > 0 ? `Se clasifică cu AI: ${done} din ${total} elemente…` : "Se pregătește clasificarea AI…")}
                </span>
                <span className="font-semibold text-primary">{pct}%</span>
              </div>
              <Progress value={pct} />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { reset(); }} data-testid="import-again-btn">Importă altă arhivă</Button>
              {projs.length === 1 && (
                <Button onClick={() => { const pid = projs[0].project_id; setOpen(false); reset(); navigate(`/projects/${pid}?tab=dosar`); }} data-testid="import-open-project-btn">
                  Deschide proiectul
                </Button>
              )}
              {projs.length !== 1 && (
                <Button onClick={() => { setOpen(false); reset(); }} data-testid="import-close-btn">Închide</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProjectDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", client: "", country: "", product_type: "", budget: 0 });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.code.trim()) return toast.error("Codul proiectului este obligatoriu (ex: HGS 1904)");
    if (!form.name.trim()) return toast.error("Numele este obligatoriu");
    try {
      const { data } = await api.post("/projects", { ...form, code: form.code.trim(), budget: Number(form.budget) || 0 });
      toast.success("Proiect creat");
      setOpen(false);
      onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-project-btn"><Plus size={16} weight="bold" className="mr-1.5" /> Proiect nou</Button>
      </DialogTrigger>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Adaugă proiect</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5"><Label>Cod proiect *</Label><Input value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="ex: HGS 1904" data-testid="project-code" /><p className="text-[11px] text-muted-foreground">Scrie codul real al proiectului. Nu se mai generează automat (ex. HGS-2026-002).</p></div>
          <div className="grid gap-1.5"><Label>Nume proiect *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="project-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Client</Label><Input value={form.client} onChange={(e) => set("client", e.target.value)} /></div>
            <div className="grid gap-1.5"><Label>Țară</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Tip produs</Label><Input value={form.product_type} onChange={(e) => set("product_type", e.target.value)} /></div>
            <div className="grid gap-1.5"><Label>Buget (€)</Label><Input type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-project-btn">Creează</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Projects() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [projects, setProjects] = useState([]);
  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const delProject = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Ștergi proiectul „${p.code} — ${p.name}"?\n\nSe șterge TOT (documente, workflow, activități), dar ajunge în Coșul de recuperare (Jurnal de acces) de unde poate fi restaurat sau șters definitiv.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Proiect mutat în Coșul de recuperare");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 fade-up" data-testid="projects-page">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Portofoliu</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tighter">Proiecte</h1>
        </div>
        {hasRole("pm", "vanzari", "asistent_vanzari") && (
          <div className="flex flex-wrap gap-2">
            <ImportOutlookDialog onDone={load} />
            <ProjectDialog onCreated={load} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {projects.map((p) => {
          const pct = p.wf_pct || 0;
          return (
            <div
              key={p.id}
              onClick={() => navigate(`/projects/${p.id}`)}
              className="cursor-pointer rounded-xl border border-border bg-card p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/50"
              data-testid={`project-card-${p.id}`}
            >
              <div className="flex items-start justify-between">
                <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
                <div className="flex items-center gap-2">
                  {hasRole("admin") && (
                    <button onClick={(e) => delProject(e, p)} className="text-muted-foreground hover:text-red-400" title="Șterge proiectul" data-testid={`project-delete-${p.id}`}>
                      <Trash size={15} />
                    </button>
                  )}
                  <RiskDot level={p.risk_level} size="lg" />
                </div>
              </div>
              {p.deadline_risk && <div className="mt-2 inline-block rounded bg-red-500/15 px-2 py-0.5 text-[11px] font-bold text-red-400" data-testid={`deadline-risk-${p.id}`}>⚠ Termen nerealist{p.recommended_deadline ? ` · recomandat ~${p.recommended_deadline}` : ""}</div>}
              <h3 className="mt-2 font-head text-base font-bold leading-tight">{p.name}</h3>
              <div className="mt-1 text-sm text-muted-foreground">{p.client || "—"}</div>
              <div className="mt-4">
                <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                  <span className="truncate">{p.wf_stage || "—"}</span>
                  <span>{Math.round(pct)}%</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-white/5">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-border pt-3 text-xs">
                <span className="rounded-full bg-white/5 px-2 py-0.5">{PROJECT_STATUS[p.status]}</span>
                <span className="font-medium">{fmtMoney(p.budget)}</span>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">Termen: {fmtDate(p.contract_deadline)}</div>
            </div>
          );
        })}
        {projects.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
            Niciun proiect. Convertește un lead câștigat sau creează unul nou.
          </div>
        )}
      </div>
    </div>
  );
}
